import { z } from 'zod'

// ─── Common ──────────────────────────────────────────────────────────────────

export type Role = 'admin' | 'committee' | 'estate_manager' | 'member'

export function isStaffRole(role: Role): boolean {
  return role === 'admin' || role === 'committee' || role === 'estate_manager'
}

export function isOperationalRole(role: Role): boolean {
  return role === 'admin' || role === 'estate_manager'
}

export function isCommitteeRole(role: Role): boolean {
  return role === 'admin' || role === 'committee'
}

// ─── Feature gating ──────────────────────────────────────────────────────────

/** A whole gateable nav section — matches the sidebar 1:1. Dashboard and each role's own
 * personal Bills & Payments / My Plot self-service entries are never gateable, so they have
 * no key here. Admin is never gated (checked in code, not data) and has no matrix row. */
export const FEATURE_KEYS = [
  'members',
  'plots',
  'billing',
  'payments',
  'approvals',
  'announcements',
  'governance',
  'service_requests',
  'documents',
] as const

export type FeatureKey = (typeof FEATURE_KEYS)[number]

export const featureKeySchema = z.enum(FEATURE_KEYS)

/** A role's resolved on/off state per feature — override (if any) already applied. */
export type FeatureAccessMap = Record<FeatureKey, boolean>

/** Gateable roles only — admin is never gated and has no row in the matrix. */
export const GATEABLE_ROLES = ['member', 'committee', 'estate_manager'] as const
export type GateableRole = (typeof GATEABLE_ROLES)[number]

export const updateRoleFeatureAccessSchema = z.object({
  role: z.enum(GATEABLE_ROLES),
  featureKey: featureKeySchema,
  enabled: z.boolean(),
})

export const updateMemberFeatureOverrideSchema = z.object({
  memberId: z.string().min(1),
  featureKey: featureKeySchema,
  enabled: z.boolean().nullable(),
})

export interface RoleFeatureAccessRow {
  role: GateableRole
  featureKey: FeatureKey
  enabled: boolean
}

export interface MemberFeatureOverrideRow {
  memberId: string
  memberName: string
  role: GateableRole
  featureKey: FeatureKey
  enabled: boolean
}

export type MemberStatus = 'active' | 'inactive' | 'invited'

/** A plot's development classification — drives per-tier utility pricing (see UtilityType.pricing).
 * 'developer': broken ground/building, or already moved in. 'owner': hasn't broken ground yet. */
export type PlotDevelopmentStatus = 'developer' | 'owner'
export const plotDevelopmentStatusSchema = z.enum(['developer', 'owner'])

/** Status values an admin may set via PATCH (not invited — that is set on create / accept-invite). */
export type ManageableMemberStatus = 'active' | 'inactive'
export type PlotStatus = 'occupied' | 'reserved' | 'available'
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'voided'
export type InvoiceType = 'standard' | 'credit_note' | 'supplementary'
export type PaymentMethod =
  | 'mpesa'
  | 'cash'
  | 'bank_transfer'
  | 'manual_mpesa'
  | 'credit_note'
  | 'c2b_mpesa'
export type BillingMethod = 'meter_based' | 'flat_fee'
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'failed'

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const DEFAULT_BOOTSTRAP_PASSWORD = 'Riverpark123!'

export const loginSchema = z.object({
  identifier: z.string().min(1, 'Email or phone number is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  rememberMe: z.boolean().optional().default(false),
})

export const phoneSchema = z.string().regex(/^\+254\d{9}$/, 'Phone must be in +254XXXXXXXXX format')

export const passwordSchema = z
  .string()
  .min(8)
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    'Password must contain uppercase, lowercase and a number',
  )

export const forgotPasswordSchema = z.object({
  identifier: z.string().min(1, 'Email or phone number is required'),
})

export const verifyPasswordOtpSchema = z.object({
  identifier: z.string().min(1, 'Phone number is required'),
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your SMS'),
})

export const phoneChangeRequestSchema = z.object({
  newPhone: z.string().min(1, 'Phone number is required'),
})

export const phoneChangeVerifySchema = z.object({
  newPhone: z.string().min(1, 'Phone number is required'),
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your SMS'),
})

export const emailChangeRequestSchema = z.object({
  newEmail: z.string().email('Invalid email address'),
})

export const emailChangeVerifySchema = z.object({
  newEmail: z.string().email('Invalid email address'),
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your email'),
})

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1),
    password: passwordSchema,
    confirmPassword: z.string().min(8),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const inviteMemberSchema = z.object({
  email: z.string().email(),
  name: z.string().min(2),
  phone: phoneSchema,
  role: z.enum(['member', 'committee', 'estate_manager']).default('member'),
  plotIds: z.array(z.string()).optional(),
  sendSms: z.boolean().optional().default(true),
})

export const acceptInviteSchema = z
  .object({
    token: z.string().min(1),
    name: z.string().min(2),
    phone: phoneSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(8),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

// Per-classification (developer/owner) pricing tier. A utility type may omit a tier entirely
// (null) to mean it simply doesn't bill that classification — e.g. Borehole Water might have no
// 'owner' tier since undeveloped land doesn't consume water.
export const utilityTypeTierSchema = z.object({
  baseRate: z.number().positive().optional(), // meter_based only
  standardCharge: z.number().nonnegative().optional(), // meter_based only
  flatAmount: z.number().positive().optional(), // flat_fee only
})

export const utilityTypePricingInputSchema = z.object({
  developer: utilityTypeTierSchema.nullable().optional(),
  owner: utilityTypeTierSchema.nullable().optional(),
})

function tierSatisfiesBillingMethod(
  tier: z.infer<typeof utilityTypeTierSchema> | null | undefined,
  billingMethod: 'meter_based' | 'flat_fee',
): boolean {
  if (!tier) return true
  return billingMethod === 'meter_based' ? !!tier.baseRate : !!tier.flatAmount
}

export const createUtilityTypeSchema = z
  .object({
    name: z.string().min(2),
    invoicePrefix: z
      .string()
      .min(2)
      .max(4)
      .regex(/^[A-Z0-9]+$/, 'Prefix must be 2–4 uppercase letters or numbers'),
    description: z.string().max(500).optional(),
    billingMethod: z.enum(['meter_based', 'flat_fee']),
    unitLabel: z.string().optional(),
    frequency: z.enum(['monthly', 'quarterly', 'annually']).default('monthly'),
    generationMode: z.enum(['manual', 'auto']).default('manual'),
    billingDay: z.number().int().min(1).max(28).default(1),
    pricing: utilityTypePricingInputSchema,
  })
  .refine((d) => !!d.pricing.developer || !!d.pricing.owner, {
    message: 'Provide pricing for at least one of Developer or Owner',
    path: ['pricing'],
  })
  .refine(
    (d) =>
      tierSatisfiesBillingMethod(d.pricing.developer, d.billingMethod) &&
      tierSatisfiesBillingMethod(d.pricing.owner, d.billingMethod),
    {
      message: 'Provide baseRate for meter_based, or flatAmount for flat_fee, in every tier set',
      path: ['pricing'],
    },
  )
  .refine((d) => d.billingMethod !== 'meter_based' || !!d.unitLabel, {
    message: 'Unit label is required for meter-based utility types',
    path: ['unitLabel'],
  })

export const updateUtilityTypeSchema = z.object({
  name: z.string().min(2).optional(),
  description: z.string().max(500).optional(),
  invoicePrefix: z
    .string()
    .min(2)
    .max(4)
    .regex(/^[A-Z0-9]+$/, 'Prefix must be 2–4 uppercase letters or numbers')
    .optional(),
  unitLabel: z.string().optional(),
  frequency: z.enum(['monthly', 'quarterly', 'annually']).optional(),
  generationMode: z.enum(['manual', 'auto']).optional(),
  billingDay: z.number().int().min(1).max(28).optional(),
  pricing: utilityTypePricingInputSchema.optional(),
})

export interface UtilityTypeTier {
  baseRate: number | null
  standardCharge: number | null
  flatAmount: number | null
}

export interface UtilityType {
  id: string
  name: string
  description: string | null
  invoicePrefix: string
  billingMethod: 'meter_based' | 'flat_fee'
  pricing: {
    developer: UtilityTypeTier | null
    owner: UtilityTypeTier | null
  }
  unitLabel: string | null
  frequency: 'monthly' | 'quarterly' | 'annually'
  generationMode: 'manual' | 'auto'
  billingDay: number
  active: boolean
  invoiceCount: number
  readingCount: number
  createdAt: number
}

export const billingPeriodSchema = z
  .string()
  .regex(/^\d{4}(-\d{2})?$/, 'Format: YYYY-MM (monthly/quarterly) or YYYY (annual)')

/** Meter readings can be fractional (analog dials) — round the subtraction to avoid float artifacts like 30.14 - 30 = 0.14000000000000057. */
export function roundMeterUnits(value: number): number {
  return Math.round(value * 100) / 100
}

export const createMeterReadingSchema = z
  .object({
    utilityTypeId: z.string(),
    plotId: z.string(),
    billingPeriod: billingPeriodSchema,
    previousReading: z.coerce.number().min(0),
    currentReading: z.coerce.number().min(0),
    notes: z.string().optional(),
  })
  .refine((d) => d.currentReading >= d.previousReading, {
    message: 'Current reading must be ≥ previous reading',
    path: ['currentReading'],
  })

export const updateMeterReadingSchema = z.object({
  previousReading: z.coerce.number().min(0).optional(),
  currentReading: z.coerce.number().min(0).optional(),
  notes: z.string().optional(),
})

export type MeterReadingStatus =
  | 'not_entered'
  | 'draft'
  | 'posted'
  | 'pending_correction'
  /** Bulk CSV import (#57) created this reading before a photo was attached. */
  | 'photo_pending'

export interface MeterReading {
  id: string
  utilityTypeId: string
  plotId: string
  billingPeriod: string
  previousReading: number
  currentReading: number
  unitsConsumed: number
  rateSnapshot: number
  /** The utility type's standardCharge locked in at the same moment as rateSnapshot — 0 if that
   * utility type has no standing charge. See billing.ts's amount formula for how it's used. */
  standardChargeSnapshot: number
  /** Null only while status is 'photo_pending' (bulk import, photo not yet attached). */
  photoR2Key: string | null
  recordedBy: string
  status: Exclude<MeterReadingStatus, 'not_entered'>
  notes: string | null
  invoiceId: string | null
  createdAt: number
  updatedAt: number
}

/** The minimal, invoice-facing view of the reading behind a meter-based invoice — lets a member
 * verify their own bill. Null when the invoice is for a flat-fee utility type (never had one). */
export interface InvoiceMeterReading {
  previousReading: number
  currentReading: number
  unitsConsumed: number
  ratePerUnit: number
  /** Locked-in standing charge for this specific invoice — 0 if none, matching
   * standardChargeSnapshot. Always present so display code never needs a null check. */
  standardCharge: number
  unitLabel: string | null
  hasPhoto: boolean
}

/** One row per plot for a given utility type + billing period — entered or not. */
export interface PlotReadingStatus {
  plotId: string
  plotOldNumber: string
  plotNumber: string
  memberName: string | null
  status: MeterReadingStatus
  reading: MeterReading | null
  /** Prefilled default for the "previous reading" field when no draft exists yet. */
  suggestedPreviousReading: number
}

// ─── Meter reading bulk import ──────────────────────────────────────────────

export const METER_READING_IMPORT_MAX_ROWS = 500

export const meterReadingImportRowInputSchema = z.object({
  rowId: z.string().min(1),
  plotNewNumber: z.string(),
  previousReading: z.string(),
  currentReading: z.string(),
})

export const meterReadingImportRequestSchema = z.object({
  utilityTypeId: z.string(),
  billingPeriod: billingPeriodSchema,
  rows: z.array(meterReadingImportRowInputSchema).min(1).max(METER_READING_IMPORT_MAX_ROWS),
})

export const meterReadingImportConfirmSchema = meterReadingImportRequestSchema

export type MeterReadingImportRowInput = z.infer<typeof meterReadingImportRowInputSchema>

export type MeterReadingImportPreviewStatus = 'valid' | 'invalid'

export interface MeterReadingImportPreviewRow {
  rowId: string
  plotNewNumber: string
  plotId: string | null
  previousReading: number | null
  currentReading: number | null
  status: MeterReadingImportPreviewStatus
  errors: string[]
}

export interface MeterReadingImportPreviewResult {
  rows: MeterReadingImportPreviewRow[]
  summary: {
    total: number
    valid: number
    invalid: number
    /** Occupied plots with no row in the uploaded file — informational, not blocking. */
    missingPlots: string[]
  }
}

export interface MeterReadingImportResult {
  imported: number
  skipped: number
}

export const generateInvoicesSchema = z.object({
  utilityTypeId: z.string(),
  billingPeriod: billingPeriodSchema,
})

export const createBillingCycleSchema = z.object({
  utilityTypeId: z.string(),
  billingPeriod: billingPeriodSchema,
})

export const markDraftInvoicesAsSentSchema = z.object({
  utilityTypeId: z.string().optional(),
})

/** Manual correction against a paid invoice — negative = credit note (overpaid), positive = supplementary (underpaid). */
export const createInvoiceAdjustmentSchema = z.object({
  amount: z.number().refine((v) => v !== 0, 'Adjustment amount cannot be zero'),
  notes: z.string().max(500).optional(),
  /** Required when the caller is estate_manager — the action is queued for committee approval instead of executing. */
  reason: z.string().min(1).max(500).optional(),
})

/** Void is otherwise parameterless; estate_manager must supply a reason since it's queued for approval. */
export const voidInvoiceSchema = z.object({
  reason: z.string().min(1).max(500).optional(),
})

/** Deactivation is otherwise parameterless; estate_manager must supply a reason since it's queued for approval. */
export const deactivateMemberSchema = z.object({
  reason: z.string().min(1).max(500).optional(),
})

export interface MemberBalance {
  /** Positive = member owes money, negative = member is in credit. */
  balance: number
}

export interface AppSettings {
  smsEnabled: boolean
  billingEmailEnabled: boolean
}

export const updateAppSettingsSchema = z.object({
  smsEnabled: z.boolean().optional(),
  billingEmailEnabled: z.boolean().optional(),
})

/**
 * Internal, app-owned SMS/email credit budget — not a mirror of Africa's Talking or ZeptoMail's
 * real balance. Gates bulk/broadcast sends (announcement + invoice-batch SMS, announcement email)
 * so a job that would run out mid-send refuses to start at all; single transactional sends
 * (payment confirmations, OTPs, invites) are not gated by it.
 */
export type NotificationChannel = 'sms' | 'email'

export interface CreditBalances {
  sms: number
  email: number
}

export interface CreditLedgerEntry {
  id: string
  channel: NotificationChannel
  delta: number
  reason: string
  createdBy: string | null
  createdAt: number
}

export const addCreditsSchema = z.object({
  channel: z.enum(['sms', 'email']),
  amount: z.number().int().positive(),
  reason: z.string().min(1).max(200),
})

/** A billing period someone has started working on — exists independent of whether it's ever been generated. */
export interface BillingCycle {
  id: string
  utilityTypeId: string
  billingPeriod: string
  createdBy: string
  createdAt: number
}

/**
 * Payments target a member, not a specific invoice — applied oldest-invoice-first across
 * everything they owe (not just paid/voided), overflow cascading to the next, per the FIFO
 * policy. The engine may create more than one `payments` row from a single recorded amount.
 */
export const recordPaymentSchema = z.object({
  memberId: z.string(),
  amount: z.number().positive(),
  method: z.enum(['cash', 'bank_transfer', 'manual_mpesa']),
  reference: z.string().optional(),
  paidAt: z.string().datetime().optional(),
  notes: z.string().max(500).optional(),
  // Defaults to the FIFO-oldest-across-everything cascade when omitted. At most one of these two
  // should be set — targeting both a specific invoice AND a utility type doesn't mean anything.
  targetInvoiceId: z.string().optional(),
  targetUtilityTypeId: z.string().optional(),
})

export const resolveC2BTransactionSchema = z.object({
  memberId: z.string(),
  targetInvoiceId: z.string().optional(),
  targetUtilityTypeId: z.string().optional(),
})

/** Optional phone override for STK push — defaults to the member's registered number when
 * omitted. Lets a member pay from a different phone (e.g. a shared or spouse's line).
 * targetInvoiceId/targetUtilityTypeId mirror recordPaymentSchema — omit both for the default
 * whole-balance FIFO push; set at most one to instead pay just that invoice or utility type. */
export const initiateStkPushSchema = z.object({
  phone: phoneSchema.optional(),
  targetInvoiceId: z.string().optional(),
  targetUtilityTypeId: z.string().optional(),
})

export const createAnnouncementSchema = z.object({
  title: z.string().min(3).max(200),
  body: z.string().min(10).max(20000),
  category: z.enum(['general', 'urgent', 'maintenance', 'financial', 'governance']),
  pinned: z.boolean().optional().default(false),
  public: z.boolean().optional().default(false),
  /** Drafts skip SMS/email/publishedAt entirely until explicitly published. */
  status: z.enum(['draft', 'published']).optional().default('published'),
  sendSms: z.boolean().optional().default(false),
  sendEmail: z.boolean().optional().default(false),
})

/** Also used to publish an existing draft (status: 'draft' -> 'published'), which is why
 * sendSms/sendEmail stay available here too — they only take effect on that transition. */
export const updateAnnouncementSchema = createAnnouncementSchema.partial()

export type AnnouncementCategory = 'general' | 'urgent' | 'maintenance' | 'financial' | 'governance'
export type AnnouncementStatus = 'draft' | 'published'

export interface Announcement {
  id: string
  title: string
  body: string
  category: AnnouncementCategory
  pinned: boolean
  public: boolean
  status: AnnouncementStatus
  smsSent: boolean
  publishedAt: number | null
  createdBy: string
  authorName: string
  createdAt: number
  updatedAt: number
  /** Only populated on single-item fetches (getAnnouncement/getPublicAnnouncement), never on lists. */
  attachments: EstateDocument[]
  /** Linked polls/resolutions, resolved with enough data to render summary cards. Only populated
   * on single-item fetches. */
  links: AnnouncementLink[]
}

export const listAnnouncementsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  category: z.enum(['general', 'urgent', 'maintenance', 'financial', 'governance']).optional(),
})

export const createServiceRequestSchema = z.object({
  type: z.enum(['maintenance', 'billing_query', 'complaint', 'general']),
  subject: z.string().min(5).max(200),
  body: z.string().min(20),
})

/** Unauthenticated submission (QR code / public report page) — no session, so the reporter
 * identifies their plot by number instead of it being implied by who's logged in, and may
 * optionally leave a way for staff to reach them back. */
export const createPublicServiceRequestSchema = z.object({
  type: z.enum(['maintenance', 'billing_query', 'complaint', 'general']),
  subject: z.string().min(5).max(200),
  body: z.string().min(20).max(2000),
  plotNumber: z.string().max(20).optional(),
  reporterName: z.string().max(100).optional(),
  reporterContact: z.string().max(100).optional(),
})

export type ServiceRequestType = 'maintenance' | 'billing_query' | 'complaint' | 'general'
export type ServiceRequestStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
export type ServiceRequestSource = 'portal' | 'public'

export interface RequestComment {
  id: string
  requestId: string
  authorId: string
  authorName: string
  body: string
  createdAt: number
}

export interface ServiceRequest {
  id: string
  memberId: string | null
  memberName: string | null
  plotId: string | null
  plotNumber: string | null
  reporterName: string | null
  reporterContact: string | null
  source: ServiceRequestSource
  type: ServiceRequestType
  subject: string
  body: string
  status: ServiceRequestStatus
  assignedTo: string | null
  createdAt: number
  updatedAt: number
}

export interface RequestAttachment {
  id: string
  requestId: string
  mimeType: string
  sizeBytes: number
  createdAt: number
}

export interface ServiceRequestWithComments extends ServiceRequest {
  comments: RequestComment[]
  attachments: RequestAttachment[]
}

export const updateRequestStatusSchema = z.object({
  status: z.enum(['open', 'in_progress', 'resolved', 'closed']),
})

export const createRequestCommentSchema = z.object({
  body: z.string().min(1).max(2000),
})

export const listServiceRequestsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: z.enum(['open', 'in_progress', 'resolved', 'closed']).optional(),
  type: z.enum(['maintenance', 'billing_query', 'complaint', 'general']).optional(),
})

export type DocumentCategory =
  | 'agm_minutes'
  | 'financial_report'
  | 'notice'
  | 'title_deed'
  | 'compliance'
  | 'other'
export type DocumentVisibility = 'all_members' | 'specific_member' | 'specific_plot' | 'public'

export interface EstateDocument {
  id: string
  title: string
  category: DocumentCategory
  mimeType: string
  sizeBytes: number | null
  visibility: DocumentVisibility
  memberId: string | null
  plotId: string | null
  plotNumber: string | null
  announcementId: string | null
  documentRequestItemId: string | null
  purpose: 'attachment' | 'inline'
  uploadedBy: string
  uploadedByName: string
  createdAt: number
}

export const createDocumentSchema = z
  .object({
    title: z.string().min(3).max(200),
    category: z.enum(['agm_minutes', 'financial_report', 'notice', 'title_deed', 'other']),
    visibility: z
      .enum(['all_members', 'specific_member', 'specific_plot', 'public'])
      .default('all_members'),
    memberId: z.string().optional(),
    plotId: z.string().optional(),
  })
  .refine((d) => d.visibility !== 'specific_member' || !!d.memberId, {
    message: 'memberId is required when visibility is specific_member',
    path: ['memberId'],
  })
  .refine((d) => d.visibility !== 'specific_plot' || !!d.plotId, {
    message: 'plotId is required when visibility is specific_plot',
    path: ['plotId'],
  })

export const listDocumentsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  category: z.enum(['agm_minutes', 'financial_report', 'notice', 'title_deed', 'other']).optional(),
})

export const createPollSchema = z.object({
  title: z.string().min(5),
  description: z.string().optional(),
  votingUnit: z.enum(['one_plot_one_vote', 'one_member_one_vote']).default('one_plot_one_vote'),
  startAt: z.string().datetime(),
  endAt: z.string().datetime(),
  options: z
    .array(z.object({ label: z.string().min(1) }))
    .min(2)
    .max(10),
})

export type VotingUnit = 'one_plot_one_vote' | 'one_member_one_vote'
export type PollStatus = 'upcoming' | 'open' | 'closed'

export interface PollOption {
  id: string
  label: string
  orderIndex: number
  /** Only populated once the poll has closed — results stay hidden while voting is open. */
  voteCount: number | null
}

export interface Poll {
  id: string
  title: string
  description: string | null
  votingUnit: VotingUnit
  startAt: number
  endAt: number
  status: PollStatus
  totalVotes: number
  createdBy: string
  authorName: string
  createdAt: number
  /** Only populated on single-item fetches, never on lists. */
  options: PollOption[]
  /** The option id the current viewer voted for, or null. Only populated on single-item fetches. */
  votedOptionId: string | null
}

export const castVoteSchema = z.object({
  optionId: z.string(),
})

export const listPollsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: z.enum(['upcoming', 'open', 'closed']).optional(),
})

export interface Resolution {
  id: string
  title: string
  body: string
  pollId: string | null
  pollTitle: string | null
  passedAt: number | null
  createdBy: string
  authorName: string
  createdAt: number
}

export const createResolutionSchema = z.object({
  title: z.string().min(5).max(200),
  body: z.string().min(10).max(20000),
  pollId: z.string().optional(),
  /** Omit for a brand-new resolution (defaults to now) — set explicitly when backfilling one
   * that predates the portal or never went through a poll. */
  passedAt: z.string().datetime().optional(),
})

export const updateResolutionSchema = createResolutionSchema.partial()

export const listResolutionsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
})

export type AnnouncementLinkEntityType = 'poll' | 'resolution'

/** A poll or resolution linked to an announcement, resolved with just enough data to render a
 * live summary card inline — not just a bare link. */
export interface AnnouncementLink {
  id: string
  entityType: AnnouncementLinkEntityType
  entityId: string
  title: string
  /** Only set for a poll link. */
  pollStatus: PollStatus | null
  /** Only set for a poll link once it's closed — options + tallies to render as result bars. */
  pollOptions: PollOption[] | null
  pollTotalVotes: number | null
  /** Only set for a resolution link. */
  resolutionExcerpt: string | null
  resolutionPassedAt: number | null
}

export const createAnnouncementLinkSchema = z.object({
  entityType: z.enum(['poll', 'resolution']),
  entityId: z.string(),
})

export const listMembersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  search: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'inactive', 'invited']).optional(),
  role: z.enum(['admin', 'committee', 'estate_manager', 'member']).optional(),
})

export const updateMemberSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  email: z.string().email().optional(),
  phone: phoneSchema.optional(),
  role: z.enum(['admin', 'committee', 'estate_manager', 'member']).optional(),
  status: z.enum(['active', 'inactive']).optional(),
  plotIds: z.array(z.string()).optional(),
})

export const completeOnboardingSchema = z
  .object({
    name: z.string().min(2).max(120),
    email: z.string().email('Invalid email address'),
    phone: phoneSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(8),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((d) => d.password !== DEFAULT_BOOTSTRAP_PASSWORD, {
    message: 'Choose a different password than the default temporary password',
    path: ['password'],
  })
  .refine((d) => !d.email.toLowerCase().endsWith('@phone.riverpark.estate'), {
    message: 'Enter your personal email address',
    path: ['email'],
  })

// ─── Member bulk import ───────────────────────────────────────────────────────

export const MEMBER_IMPORT_MAX_ROWS = 500

const plotMeterNumberSchema = z.string().max(64).optional()

export const memberImportRowInputSchema = z.object({
  rowId: z.string().min(1),
  name: z.string(),
  phone: z.string(),
  email: z.string().optional(),
  plotNumber: z.string().optional(),
  boreholeMeterNumber: plotMeterNumberSchema,
  kplcMeterNumber: plotMeterNumberSchema,
})

export const memberImportRequestSchema = z.object({
  rows: z.array(memberImportRowInputSchema).min(1).max(MEMBER_IMPORT_MAX_ROWS),
})

export const memberImportConfirmSchema = memberImportRequestSchema.extend({
  notify: z.boolean().optional().default(false),
})

export const memberImportNotifySchema = z.object({
  memberIds: z.array(z.string().min(1)).min(1).max(MEMBER_IMPORT_MAX_ROWS),
})

export type MemberImportRowInput = z.infer<typeof memberImportRowInputSchema>

export type MemberImportPreviewStatus = 'valid' | 'invalid' | 'warning'

export interface MemberImportPreviewRow {
  rowId: string
  name: string
  phone: string
  email: string | null
  plotNumber: string | null
  plotId: string | null
  plotLabel: string | null
  boreholeMeterNumber: string | null
  kplcMeterNumber: string | null
  status: MemberImportPreviewStatus
  errors: string[]
  warnings: string[]
}

export interface MemberImportPreviewResult {
  rows: MemberImportPreviewRow[]
  summary: {
    total: number
    valid: number
    invalid: number
    warnings: number
  }
}

export interface MemberImportResult {
  imported: number
  skipped: number
  memberIds: string[]
  notifyPending: boolean
  notified: number
  smsSent?: number
  smsFailed?: number
  emailsSent?: number
}

// ─── Plots ────────────────────────────────────────────────────────────────────

export type PlotType = 'residential' | 'special_use'

export const listPlotsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  status: z.enum(['occupied', 'reserved', 'available']).optional(),
  section: z.string().optional(),
  plotType: z.enum(['residential', 'special_use']).optional(),
})

export const createPlotSchema = z.object({
  newNumber: z.string().min(1).max(64),
  oldNumber: z.string().min(1).max(64).optional(),
  plotType: z.enum(['residential', 'special_use']).default('residential'),
  developmentStatus: plotDevelopmentStatusSchema.default('owner'),
  section: z.string().max(64).optional(),
  mainAvenue: z.string().max(120).optional(),
  courtOrStreet: z.string().max(120).optional(),
  notes: z.string().max(500).optional(),
  boreholeMeterNumber: plotMeterNumberSchema,
  kplcMeterNumber: plotMeterNumberSchema,
  status: z.enum(['occupied', 'reserved', 'available']).optional(),
})

export const updatePlotSchema = z.object({
  developmentStatus: plotDevelopmentStatusSchema.optional(),
  section: z.string().max(64).optional(),
  mainAvenue: z.string().max(120).optional(),
  courtOrStreet: z.string().max(120).optional(),
  notes: z.string().max(500).optional(),
  boreholeMeterNumber: plotMeterNumberSchema,
  kplcMeterNumber: plotMeterNumberSchema,
  status: z.enum(['occupied', 'reserved', 'available']).optional(),
})

export const assignPlotOwnerSchema = z.object({
  memberId: z.string().min(1),
  transferNote: z.string().max(500).optional(),
  /** Admin-only escape hatch — required to proceed when the plot has an outstanding balance. */
  overrideDuesReason: z.string().min(1).max(500).optional(),
})

export const releasePlotOwnerSchema = z.object({
  transferNote: z.string().max(500).optional(),
  overrideDuesReason: z.string().min(1).max(500).optional(),
})

// ─── Plot bulk import ─────────────────────────────────────────────────────────

export const PLOT_IMPORT_MAX_ROWS = 500

export const plotImportRowInputSchema = z.object({
  rowId: z.string().min(1),
  plotNumber: z.string(),
  mainAvenue: z.string().optional(),
  courtOrStreet: z.string().optional(),
  section: z.string().optional(),
  notes: z.string().optional(),
  boreholeMeterNumber: plotMeterNumberSchema,
  kplcMeterNumber: plotMeterNumberSchema,
})

export const plotImportRequestSchema = z.object({
  rows: z.array(plotImportRowInputSchema).min(1).max(PLOT_IMPORT_MAX_ROWS),
})

export const plotImportConfirmSchema = plotImportRequestSchema

export type PlotImportRowInput = z.infer<typeof plotImportRowInputSchema>

export type PlotImportPreviewStatus = 'valid' | 'invalid' | 'warning'

export interface PlotImportPreviewRow {
  rowId: string
  plotNumber: string
  newNumber: string | null
  mainAvenue: string | null
  courtOrStreet: string | null
  section: string | null
  notes: string | null
  boreholeMeterNumber: string | null
  kplcMeterNumber: string | null
  plotType: PlotType | null
  existingPlotId: string | null
  status: PlotImportPreviewStatus
  errors: string[]
  warnings: string[]
}

export interface PlotImportPreviewResult {
  rows: PlotImportPreviewRow[]
  summary: {
    total: number
    valid: number
    invalid: number
    warnings: number
  }
}

export interface PlotImportResult {
  imported: number
  updated: number
  plotIds: string[]
}

export interface PlotOwnerSummary {
  memberId: string
  name: string
}

/** Surfaced on the invoice/plot itself so the UI can show a pending-approval chip without a separate lookup. */
export interface PendingApprovalRef {
  id: string
  actionType: ApprovalActionType
  createdAt: number
}

export interface PlotListItem {
  id: string
  oldNumber: string
  newNumber: string
  status: PlotStatus
  plotType: PlotType
  developmentStatus: PlotDevelopmentStatus
  section: string | null
  mainAvenue: string | null
  courtOrStreet: string | null
  notes: string | null
  boreholeMeterNumber: string | null
  kplcMeterNumber: string | null
  currentOwner: PlotOwnerSummary | null
  /** Invoices tied to this plot, unpaid minus paid — independent of who currently owns it. */
  outstandingBalance: number
  pendingApproval?: PendingApprovalRef | null
}

export interface PlotDetail extends PlotListItem {
  createdAt: number
  updatedAt: number
}

/** Plot record for portal members (no owner / admin-only fields). */
export type MemberPlotDetail = Omit<PlotDetail, 'currentOwner'>

export interface PlotOwnershipHistoryItem {
  id: string
  memberId: string
  memberName: string
  acquiredAt: number
  releasedAt: number | null
  transferNote: string | null
}

// ─── API response types ───────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T
  meta?: {
    total?: number
    page?: number
    limit?: number
    totalPages?: number
  }
}

export interface MemberPlotSummary {
  id: string
  oldNumber: string
  newNumber: string
  developmentStatus: PlotDevelopmentStatus
}

export interface MemberListItem {
  id: string
  name: string
  email: string | null
  phone: string
  role: Role
  status: MemberStatus
  plots: MemberPlotSummary[]
  /** Positive = owes, negative = in credit. */
  outstandingBalance: number
  lastLoginAt: number | null
  createdAt: number
}

export interface MemberDetail extends MemberListItem {
  updatedAt: number
  committeeAppointment?: CommitteeAppointment | null
}

export interface ApiError {
  message: string
  code?: string
  field?: string
}

// ─── Domain types (mirrors DB schema) ────────────────────────────────────────

export interface Member {
  id: string
  name: string
  email: string | null
  phone: string
  role: Role
  status: MemberStatus
  createdAt: number
}

export interface Plot {
  id: string
  oldNumber: string
  newNumber: string
  status: PlotStatus
  areaM2?: number
  currentOwner?: Member
}

export interface Invoice {
  id: string
  invoiceNumber: string
  memberId: string
  memberName: string
  plotId: string
  plotNumber: string
  utilityTypeId: string | null
  utilityTypeName: string | null
  generationRunId: string | null
  invoiceType: InvoiceType
  relatedInvoiceId?: string
  /** The bill a credit note / supplementary charge corrects. */
  relatedInvoice?: RelatedInvoiceRef
  billingPeriod?: string
  description: string
  amount: number
  /** Sum of payments applied so far. No stored 'partial' status — compute paid/balance instead. */
  amountPaid: number
  /** amount - amountPaid. Not necessarily >= 0 for a credit_note (negative amount). */
  balance: number
  dueDate?: number
  status: InvoiceStatus
  createdAt: number
  pendingApproval?: PendingApprovalRef | null
}

export interface RelatedInvoiceRef {
  invoiceNumber: string
  status: InvoiceStatus
  utilityTypeName: string | null
  billingPeriod?: string
}

export interface ArrearsAgingBuckets {
  current: number
  days1to30: number
  days31to60: number
  days61to90: number
  days90Plus: number
}

export type ArrearsBucket = keyof ArrearsAgingBuckets

export interface MemberArrears {
  memberId: string
  memberName: string
  memberPhone: string
  outstandingAmount: number
  invoiceCount: number
  aging: ArrearsAgingBuckets
}

export interface ArrearsAgingSummary {
  current: { total: number; memberCount: number }
  days1to30: { total: number; memberCount: number }
  days31to60: { total: number; memberCount: number }
  days61to90: { total: number; memberCount: number }
  days90Plus: { total: number; memberCount: number }
}

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export const listInvoicesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: z.enum(['draft', 'sent', 'paid', 'overdue', 'voided']).optional(),
  utilityTypeId: z.string().optional(),
  memberId: z.string().optional(),
  invoiceType: z.enum(['standard', 'supplementary', 'credit_note']).optional(),
  excludeInvoiceType: z.enum(['standard', 'supplementary', 'credit_note']).optional(),
  billingPeriod: billingPeriodSchema.optional(),
})

export const listArrearsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  bucket: z.enum(['current', 'days1to30', 'days31to60', 'days61to90', 'days90Plus']).optional(),
})

export type GenerationRunStatus = 'pending' | 'running' | 'completed' | 'failed'
export type GenerationMode = 'manual' | 'auto'

export interface GenerationRun {
  id: string
  utilityTypeId: string
  billingPeriod: string
  triggeredBy: string | null
  mode: GenerationMode
  status: GenerationRunStatus
  invoicesCreated: number
  plotsSkipped: number
  plotsPendingReading: number
  plotsNotApplicable: number
  errorLog: string | null
  createdAt: number
  completedAt: number | null
}

export interface Payment {
  id: string
  invoiceId: string
  invoiceNumber: string
  memberId: string
  memberName: string
  amount: number
  method: PaymentMethod
  mpesaReceiptNumber?: string
  reference?: string
  notes?: string
  recordedBy: string | null
  paidAt: number
  createdAt: number
}

/** One payment recording call can touch several invoices (oldest-first) — this is the breakdown. */
export interface PaymentApplication {
  invoiceId: string
  invoiceNumber: string
  amountApplied: number
  invoiceStatus: InvoiceStatus
}

export interface RecordPaymentResult {
  applications: PaymentApplication[]
  /** Excess after covering every outstanding invoice — recorded as a credit_note invoice. */
  creditAmount: number
  creditInvoiceId: string | null
}

export const paymentsReportQuerySchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
  utilityTypeId: z.string().optional(),
  method: z
    .enum(['mpesa', 'cash', 'bank_transfer', 'manual_mpesa', 'credit_note', 'c2b_mpesa'])
    .optional(),
})

export const estateReportQuerySchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
})

export interface MonthlyCollectionsPoint {
  month: string
  totalCollected: number
  count: number
}

export interface PaymentsSummaryRow {
  date: string
  totalCollected: number
  count: number
}

export interface BillingReconciliation {
  totalBilled: number
  totalCollected: number
}

export interface UtilityTypeBreakdownRow {
  utilityTypeId: string
  utilityTypeName: string
  totalBilled: number
  totalCollected: number
}

export interface PaymentMethodBreakdownRow {
  method: PaymentMethod
  totalCollected: number
  count: number
}

export interface EstateReportData {
  from: number
  to: number
  reconciliation: BillingReconciliation
  dailySummary: PaymentsSummaryRow[]
  byUtilityType: UtilityTypeBreakdownRow[]
  byMethod: PaymentMethodBreakdownRow[]
  agingSummary: ArrearsAgingSummary
  topArrears: MemberArrears[]
}

export type MpesaTransactionStatus = 'pending' | 'successful' | 'failed' | 'cancelled' | 'timeout'

export interface MpesaTransaction {
  id: string
  status: MpesaTransactionStatus
  resultDescription: string | null
  mpesaReceiptNumber: string | null
}

/** A confirmed direct-Paybill (C2B) payment staff couldn't auto-match to a member — flattened
 * from the transactions row's metadata JSON for the "Unmatched Payments" admin queue. */
export interface UnmatchedC2BTransaction {
  id: string
  amount: number
  phone: string | null
  billRefNumber: string
  payerName: string
  createdAt: number
}

export type NotificationType =
  | 'invoice_created'
  | 'payment_confirmed'
  | 'payment_failed'
  | 'overdue_reminder'
  | 'request_created'
  | 'request_update'
  | 'announcement'
  | 'approval_required'
  | 'approval_result'
  | 'document_requested'
  | 'document_request_update'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  body: string
  entityType?: string
  entityId?: string
  readAt?: number
  createdAt: number
}

// ─── Approval requests (maker-checker) ────────────────────────────────────────

export type ApprovalActionType =
  | 'void_invoice'
  | 'invoice_adjustment'
  | 'plot_ownership_override'
  | 'member_deactivate'

export type ApprovalEntityType = 'invoice' | 'plot' | 'member'

export interface ApprovalRequest {
  id: string
  actionType: ApprovalActionType
  entityType: ApprovalEntityType
  entityId: string
  summary: string
  reason: string
  status: ApprovalStatus
  initiatedBy: string
  initiatedByName: string
  createdAt: number
  reviewedBy: string | null
  reviewedByName: string | null
  reviewNote: string | null
  reviewedAt: number | null
}

export const decideApprovalSchema = z.object({
  note: z.string().max(500).optional(),
})

// ─── Document requests (staff asks a member for compliance paperwork) ─────────

export type DocumentRequestStatus =
  | 'pending'
  | 'submitted'
  | 'needs_revision'
  | 'approved'
  | 'cancelled'

export type DocumentRequestItemStatus = 'pending' | 'submitted' | 'approved' | 'rejected'

export interface DocumentRequestItem {
  id: string
  requestId: string
  label: string
  status: DocumentRequestItemStatus
  documentId: string | null
  documentTitle: string | null
  documentMimeType: string | null
  reviewNote: string | null
  decidedBy: string | null
  decidedByName: string | null
  decidedAt: number | null
  createdAt: number
}

export interface DocumentRequest {
  id: string
  plotId: string
  plotNumber: string
  memberId: string
  memberName: string
  requestedBy: string
  requestedByName: string
  title: string
  instructions: string
  status: DocumentRequestStatus
  dueDate: number | null
  createdAt: number
  items: DocumentRequestItem[]
}

export const createDocumentRequestSchema = z.object({
  plotId: z.string().min(1),
  title: z.string().min(3).max(200),
  instructions: z.string().max(2000).optional().default(''),
  items: z
    .array(z.object({ label: z.string().min(1).max(200) }))
    .min(1)
    .max(20),
  dueDate: z.string().datetime().optional(),
})

export const decideDocumentRequestItemSchema = z.object({
  note: z.string().max(500).optional(),
})

// ─── Committee appointments ───────────────────────────────────────────────────

export type CommitteePosition =
  | 'chairman'
  | 'vice_chairman'
  | 'treasurer'
  | 'secretary'
  | 'committee_member'

export const COMMITTEE_POSITION_LABELS: Record<CommitteePosition, string> = {
  chairman: 'Chairman',
  vice_chairman: 'Vice Chairman',
  treasurer: 'Treasurer',
  secretary: 'Secretary',
  committee_member: 'Committee Member',
}

export interface CommitteeAppointment {
  id: string
  memberId: string
  memberName: string
  position: CommitteePosition
  startedAt: number
  endedAt: number | null
  appointedBy: string | null
  appointedByName: string | null
  notes: string | null
  createdAt: number
}

export const appointMemberSchema = z.object({
  memberId: z.string().min(1),
  position: z.enum(['chairman', 'vice_chairman', 'treasurer', 'secretary', 'committee_member']),
  startedAt: z.number().int().optional(),
  notes: z.string().max(500).optional(),
})

export type AppointMemberInput = z.infer<typeof appointMemberSchema>
